import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import { type Task, task } from 'ember-concurrency';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  COMPARISON_MODES,
  type ComparisonMode,
  DATE_FORMAT,
} from 'lumen/utils/constants/net-revenue-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import { defaultComparisonFromDate } from 'lumen/utils/net-revenue/date-pickers';
import type {
  NetRevenuePeriods,
  NetRevenuePeriodsArgs,
} from 'lumen/utils/net-revenue/types';

export interface NetRevenueModel {
  periods: Task<NetRevenuePeriods, [NetRevenuePeriodsArgs]>;
}

type NetRevenueQueryParams = {
  comparisonFromDate: string | null;
  comparisonMode: ComparisonMode | null;
  fromDate: string | null;
  toDate: string | null;
};

// @ts-expect-error: Unable to resolve signature of class decorator when called as an expression.
@protectedRoute({
  features: 'app.net_revenue.view',
  featureToggles: FEATURE_TOGGLES.BI_NET_REVENUE,
})
export default class HotelNetRevenueRoute extends Route {
  queryParams = {
    comparisonFromDate: { refreshModel: true },
    comparisonMode: { refreshModel: true },
    fromDate: { refreshModel: true },
    toDate: { refreshModel: true },
  };

  @service declare hotelAndMarket: HotelAndMarketServiceWithHotel;
  @service declare netRevenue: Services['net-revenue'];

  getPeriodsTask: Task<NetRevenuePeriods, [NetRevenuePeriodsArgs]> = task(
    { restartable: true },
    async (args: NetRevenuePeriodsArgs) =>
      await this.netRevenue.getPeriods(args),
  );

  model(params: NetRevenueQueryParams): NetRevenueModel {
    const model: NetRevenueModel = { periods: this.getPeriodsTask };

    const today = dayjs();
    const fromDate =
      params.fromDate ?? today.startOf('month').format(DATE_FORMAT);
    const toDate = params.toDate ?? today.endOf('month').format(DATE_FORMAT);
    const comparisonMode: ComparisonMode =
      params.comparisonMode ?? COMPARISON_MODES.SWLY;
    const comparisonFromDate =
      params.comparisonFromDate ??
      defaultComparisonFromDate({ comparisonMode, fromDate, toDate });
    const hotelId = this.hotelAndMarket.hotel.id!;

    void model.periods.perform({
      comparisonFromDate,
      comparisonMode,
      fromDate,
      hotelId,
      toDate,
    });

    return model;
  }
}
